import React, { useState } from 'react'
import {
    Container,
    Paper,
    TextField,
    Button,
    Typography,
    Box,
    InputAdornment,
    CircularProgress,
    useTheme
} from '@mui/material'
import {
    Devices as DevicesIcon,
    VpnKey as KeyIcon
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { deviceApi } from '../../api/deviceApi'
import toast from 'react-hot-toast'
import { useLoading } from '../../hooks'

const DeviceLogin = () => {
    const navigate = useNavigate()
    const theme = useTheme()
    const { loading, withLoading } = useLoading()
    const [deviceKey, setDeviceKey] = useState('')

    const handleLogin = async (e) => {
        e.preventDefault()

        if (!deviceKey.trim()) {
            toast.error('Please enter a valid Device Key')
            return
        }

        withLoading(async () => {
            try {
                const response = await deviceApi.deviceLogin({ device_key: deviceKey }, { skipSuccessToast: true })
                const { token, device_id } = response.data

                if (!token) {
                    throw new Error('No token received from server')
                }

                // Store token specifically for device
                localStorage.setItem('device_token', token)

                // Fetch full device details to get name, location, org_id
                try {
                    const deviceDetailsRes = await deviceApi.getDevice(device_id)
                    const device = deviceDetailsRes.data
                    localStorage.setItem('device_info', JSON.stringify(device))
                    toast.success(`Connected to ${device.name}`)
                } catch (detailErr) {
                    console.warn("Could not fetch device details", detailErr)
                    // Fallback if detail fetch fails, but we have token
                    localStorage.setItem('device_info', JSON.stringify({ id: device_id, name: 'Device ' + device_id }))
                    toast.success('Device Authenticated')
                }

                navigate('/device/kiosk')

            } catch (err) {
                console.error('Device Login error:', err)
                const errorMsg = err.response?.data?.detail ||
                    err.response?.data?.message ||
                    'Connection failed. Invalid Device Key.'
                toast.error(errorMsg)
            }
        })
    }

    return (
        <Box
            sx={{
                minHeight: '100vh',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: theme.palette.mode === 'dark'
                    ? 'radial-gradient(circle at 50% 50%, #1e293b 0%, #0f172a 100%)'
                    : 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                position: 'relative',
                overflow: 'hidden',
            }}
        >
            {/* Abstract Background Shapes */}
            <Box
                sx={{
                    position: 'absolute',
                    top: '-10%',
                    right: '-5%',
                    width: '500px',
                    height: '500px',
                    borderRadius: '50%',
                    background: theme.palette.mode === 'dark'
                        ? 'radial-gradient(circle, rgba(59,130,246,0.15) 0%, rgba(0,0,0,0) 70%)'
                        : 'radial-gradient(circle, rgba(37,99,235,0.1) 0%, rgba(255,255,255,0) 70%)',
                    zIndex: 0,
                }}
            />
            <Box
                sx={{
                    position: 'absolute',
                    bottom: '-10%',
                    left: '-5%',
                    width: '400px',
                    height: '400px',
                    borderRadius: '50%',
                    background: theme.palette.mode === 'dark'
                        ? 'radial-gradient(circle, rgba(139,92,246,0.15) 0%, rgba(0,0,0,0) 70%)'
                        : 'radial-gradient(circle, rgba(124,58,237,0.1) 0%, rgba(255,255,255,0) 70%)',
                    zIndex: 0,
                }}
            />

            <Container component="main" maxWidth="xs" sx={{ position: 'relative', zIndex: 1 }}>
                <Paper
                    elevation={theme.palette.mode === 'dark' ? 4 : 0}
                    className="fade-in"
                    sx={{
                        padding: { xs: 3, sm: 5 },
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        borderRadius: 4,
                        background: theme.palette.mode === 'dark'
                            ? 'rgba(30, 41, 59, 0.7)'
                            : 'rgba(255, 255, 255, 0.8)',
                        backdropFilter: 'blur(20px)',
                        border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.5)'}`,
                        boxShadow: theme.palette.mode === 'light'
                            ? '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                            : 'none',
                    }}
                >
                    <Box
                        sx={{
                            width: 50,
                            height: 50,
                            borderRadius: 2,
                            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mb: 2,
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                        }}
                    >
                        <DevicesIcon sx={{ color: '#fff', fontSize: 28 }} />
                    </Box>

                    <Typography component="h1" variant="h5" sx={{ fontWeight: 700, mb: 1, letterSpacing: '-0.5px' }}>
                        Kiosk Mode
                    </Typography>
                    <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 4 }}>
                        Enter Device Key to activate this terminal
                    </Typography>

                    <Box component="form" onSubmit={handleLogin} sx={{ width: '100%' }}>
                        <TextField
                            margin="normal"
                            fullWidth
                            id="deviceKey"
                            label="Device Key"
                            name="deviceKey"
                            autoComplete="off"
                            autoFocus
                            value={deviceKey}
                            onChange={(e) => setDeviceKey(e.target.value)}
                            disabled={loading}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <KeyIcon color="action" fontSize="small" />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ mb: 3 }}
                        />

                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            size="large"
                            disabled={loading}
                            sx={{
                                py: 1.5,
                                fontSize: '1rem',
                                fontWeight: 600,
                                letterSpacing: '0.5px',
                                borderRadius: 2,
                            }}
                        >
                            {loading ? <CircularProgress size={24} color="inherit" /> : 'Connect Device'}
                        </Button>

                        <Box sx={{ mt: 3, textAlign: 'center' }}>
                            <Typography variant="caption" color="text.secondary">
                                Device keys are managed in the Organization Admin panel.
                            </Typography>
                        </Box>
                    </Box>
                </Paper>

                <Box sx={{ mt: 4, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                        © {new Date().getFullYear()} Face Recognition System. All rights reserved.
                    </Typography>
                </Box>
            </Container>
        </Box>
    )
}

export default DeviceLogin
